// /community/:slug — one community's curated front page. Same scoring
// engine as the personalized paper, but scoped to a single community so
// the reader sees what's happening in that one place.
//
// Shows an "Editor tools" button when the caller is a moderator/editor
// of this community, deep-linking into /communities/{slug}/dashboard.

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PostCard } from '../components/PostCard';
import { formatMastheadDate } from '../lib/format';
import { dividerLabel, olderLabel, splitByEdition } from '../lib/edition';
import type { Community as Comm, Post, PostLayout } from '../types/api';

interface Moderator {
    user_id:      string;
    display_name: string;
}

interface LayoutResp { layouts: Array<PostLayout & { post_id: string }> }

export function Community() {
    const api = useApi();
    const { slug } = useParams<{ slug: string }>();
    const { authed, login } = useAuth();
    const [community, setCommunity] = useState<Comm | null>(null);
    const [posts, setPosts] = useState<Post[]>([]);
    const [imEditor, setImEditor] = useState(false);
    const [err, setErr] = useState('');

    const load = useCallback(async () => {
        if (!authed || !slug) return;
        try {
            // Community metadata — masthead subtitle, description, etc.
            const communities = await api.get<{ communities: Comm[] }>('/api/communities');
            const c = (communities.communities || []).find(c => c.slug === slug) || null;
            setCommunity(c);

            // Curated feed for this community. /api/paper scores across all
            // communities, so we constrain here to a single community_id
            // via the existing posts endpoint. The main paper hits /api/paper;
            // this page hits the community-scoped list because the reader has
            // explicitly asked to focus on one community.
            if (c) {
                const [list, layoutRes] = await Promise.all([
                    api.get<{ posts: Post[] }>('/api/posts?community_id=' + c.id + '&limit=50'),
                    api.get<LayoutResp>('/api/communities/' + slug + '/layout'),
                ]);
                // Merge per-post layout onto each Post so the renderer can
                // key off post.layout without a second lookup.
                const overlay = new Map<string, PostLayout>();
                for (const l of layoutRes.layouts ?? []) {
                    overlay.set(l.post_id, {
                        paper_feature: l.paper_feature ?? null,
                        paper_section: l.paper_section ?? null,
                        paper_column:  l.paper_column  ?? null,
                        paper_rank:    l.paper_rank    ?? null,
                    });
                }
                setPosts((list.posts || []).map(p => ({ ...p, layout: overlay.get(p.id) })));

                // Am I a moderator/editor? Cheap check via the moderators list.
                try {
                    const mods = await api.get<{ moderators: Moderator[] }>(
                        '/api/communities/' + slug + '/moderators'
                    );
                    const me = await api.get<{ id: string }>('/api/me');
                    setImEditor((mods.moderators || []).some(m => m.user_id === me.id));
                } catch { setImEditor(false); }
            }
        } catch (e) { setErr((e as Error).message); }
    }, [api, authed, slug]);
    useEffect(() => { load(); }, [load]);

    if (!authed) return (
        <div className="prelogin">
            <p>Log in to see this community.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );
    if (err)       return <main className="paper-wrap"><p style={{ color: '#8b1919', padding: 40 }}>{err}</p></main>;
    if (!community) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading…</div>;

    return (
        <section id="view-paper">
            <div className="paper-wrap">
                <div className="masthead">
                    <div className="rule-top" />
                    <h1>The {community.name} Reader</h1>
                    <div className="issue">
                        {formatMastheadDate()} · Curated for {community.name}
                        {imEditor && (
                            <> · <Link to={'/communities/' + community.slug + '/dashboard'}
                                       style={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}>
                                       Editor tools →
                                    </Link></>
                        )}
                    </div>
                    <div className="rule-bottom" />
                </div>

                {community.tagline && (
                    <p style={{ textAlign: 'center', fontStyle: 'italic', color: '#555', margin: '12px 24px' }}>
                        {community.tagline}
                    </p>
                )}

                {posts.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#666', padding: 40, fontFamily: "'Newsreader',serif", fontStyle: 'italic' }}>
                        Nothing published in this community yet. {imEditor && <><br/>You're an editor here — post the first one.</>}
                    </p>
                ) : (
                    <CadenceLayout community={community} posts={posts} />
                )}

                <div className="colophon">
                    The {community.name} Reader · <Link to="/">← back to your paper</Link>
                </div>
            </div>
        </section>
    );
}

// CadenceLayout splits posts by the community's cadence, drawing a wide
// divider between the current edition and older posts. Applies the
// editor's manual overrides on top: hidden drops the post, hero lifts
// it to a full-width slot at the top, left/right pairs render two
// consecutive posts side-by-side. Rank sorts within the pool (nulls
// last, engagement fallback preserves existing order).
function CadenceLayout({ community, posts }: { community: Comm; posts: Post[] }) {
    // Drop hidden posts before any bucketing — editor said no, so no.
    const visible = posts.filter(p => p.layout?.paper_feature !== 'hidden');
    // Hero first: pull *all* hero-marked posts and render them stacked
    // at the top full-width. Not a "one hero at a time" system — the
    // editor may promote several across today's edition.
    const heroes = visible.filter(p => p.layout?.paper_feature === 'hero');
    const rest   = visible.filter(p => p.layout?.paper_feature !== 'hero');
    // Manual rank first, then fall through to the server's ordering.
    rest.sort((a, b) => {
        const ra = a.layout?.paper_rank ?? null;
        const rb = b.layout?.paper_rank ?? null;
        if (ra != null && rb != null) return ra - rb;
        if (ra != null) return -1;
        if (rb != null) return 1;
        return 0;
    });
    const { current, older } = splitByEdition(
        rest,
        p => p.topic_day ?? p.created_at,
        community.cadence,
    );
    return (
        <>
            {heroes.length > 0 && (
                <div className="cols">
                    <section>
                        {heroes.map(p => (
                            <div key={p.id} className="hero-slot" style={heroWrap}>
                                <PostCard post={p} />
                            </div>
                        ))}
                    </section>
                </div>
            )}
            <div className="cols">
                <section>
                    <h2>{dividerLabel(community.cadence)} in {community.name}</h2>
                    {current.length === 0 ? (
                        <p style={{ color: '#666', fontStyle: 'italic', padding: 8 }}>
                            Nothing published in this edition yet.
                        </p>
                    ) : renderWithPairs(current)}
                </section>
            </div>

            {older.length > 0 && (
                <>
                    <div className="day-divider day-divider-wide">
                        <span>{olderLabel(community.cadence)}</span>
                    </div>
                    <div className="cols cols-shelf">
                        <section>
                            {renderWithPairs(older)}
                        </section>
                    </div>
                </>
            )}
        </>
    );
}

// renderWithPairs walks the post list and, whenever a `left` post is
// immediately followed by a `right` post, renders them as a two-column
// row instead of stacked. Everything else falls through to a single
// stacked PostCard. This is the minimum viable "posts sit next to
// each other" primitive.
function renderWithPairs(posts: Post[]): React.ReactNode[] {
    const out: React.ReactNode[] = [];
    for (let i = 0; i < posts.length; i++) {
        const a = posts[i];
        const b = posts[i + 1];
        if (a.layout?.paper_column === 'left' && b?.layout?.paper_column === 'right') {
            out.push(
                <div key={a.id + '_' + b.id} style={pairRow}>
                    <div style={pairCell}><PostCard post={a} /></div>
                    <div style={pairCell}><PostCard post={b} /></div>
                </div>,
            );
            i++; // consume b
            continue;
        }
        out.push(<PostCard key={a.id} post={a} />);
    }
    return out;
}

const heroWrap: React.CSSProperties = {
    borderTop:    '2px solid #333',
    borderBottom: '1px solid #cfc9b7',
    padding:      '8px 0 16px',
    marginBottom: 16,
};
const pairRow: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16,
    marginBottom: 12,
};
const pairCell: React.CSSProperties = { minWidth: 0 };
